import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

/** Contexto visual del overlay de carga (un solo componente, varios usos). */
export type LoadingVariant = 'sign' | 'verify' | 'admin' | 'auth' | 'download' | 'gestion' | 'generic';

/** Fase del viaje de firma (solo variant=sign). */
export type SignJourneyPhase = 'upload' | 'sign' | 'prepare';

export interface SignJourneyState {
  phase?: SignJourneyPhase;
  current?: number;
  total?: number;
  fileName?: string | null;
  fileSizeBytes?: number | null;
}

interface LoadingPreset {
  title: string;
  iconClass: string;
  useSvgIcon: boolean;
  svgIconPath: string;
  coreIconClass: string;
}

const PRESETS: Record<LoadingVariant, LoadingPreset> = {
  sign: {
    title: 'Firmando documentos',
    iconClass: 'fa-pen-nib',
    useSvgIcon: true,
    svgIconPath: 'assets/icons/signature-icon.svg',
    coreIconClass: 'fa-file-signature'
  },
  verify: {
    title: 'Verificando firmas del documento',
    iconClass: 'fa-pen-nib',
    useSvgIcon: true,
    svgIconPath: 'assets/icons/signature-icon.svg',
    coreIconClass: 'fa-file-signature'
  },
  admin: {
    title: 'Administración',
    iconClass: 'fa-shield-alt',
    useSvgIcon: true,
    svgIconPath: 'assets/icons/shield-icon.svg',
    coreIconClass: 'fa-user-shield'
  },
  auth: {
    title: 'Autenticación Certifirma Digital Virtual',
    iconClass: 'fa-shield-alt',
    useSvgIcon: true,
    svgIconPath: 'assets/icons/shield-icon.svg',
    coreIconClass: 'fa-shield-alt'
  },
  download: {
    title: 'Descargando documento',
    iconClass: 'fa-download',
    useSvgIcon: false,
    svgIconPath: '',
    coreIconClass: 'fa-download'
  },
  gestion: {
    title: 'Gestión',
    iconClass: 'fa-briefcase',
    useSvgIcon: false,
    svgIconPath: '',
    coreIconClass: 'fa-clipboard-list'
  },
  generic: {
    title: 'Procesando',
    iconClass: 'fa-spinner',
    useSvgIcon: false,
    svgIconPath: '',
    coreIconClass: 'fa-circle-notch'
  }
};

const SIGN_TIPS = [
  'Los archivos grandes pueden tardar varios minutos en firmarse.',
  'No cierres ni recargues esta ventana mientras termina el proceso.',
  'Estamos enviando, firmando y preparando tu documento de forma segura.',
  'Al finalizar podrás descargar o verificar el documento firmado.'
];

const PHASE_MESSAGES: Record<SignJourneyPhase, string> = {
  upload: 'Enviando el documento al servidor…',
  sign: 'Aplicando la firma digital…',
  prepare: 'Descargando y preparando la vista del documento…'
};

/** Umbrales visuales del viaje (el back no reporta avance real). */
const UPLOAD_UNTIL = 34;
const SIGN_UNTIL = 78;
/** Techo mientras esperamos respuesta (nunca llega a 100 hasta terminar). */
const SOFT_CAP = 90;

@Component({
  selector: 'app-loading',
  templateUrl: './loading.component.html',
  styleUrls: ['./loading.component.css'],
  standalone: false
})
export class LoadingComponent implements OnInit, OnDestroy {
  mensaje = '';
  titulo = '';
  iconClass = 'fa-shield-alt';
  useSvgIcon = false;
  svgIconPath = '';
  coreIconClass = 'fa-circle-notch';
  variant: LoadingVariant = 'generic';

  /** Viaje de firma */
  isSignJourney = false;
  phase: SignJourneyPhase = 'upload';
  fileName = '';
  fileSizeBytes: number | null = null;
  currentDoc = 0;
  totalDocs = 0;
  /** Avance visual 0–100 (estimado + fases reales conocidas). */
  progressPercent = 0;
  currentTip = SIGN_TIPS[0];
  readonly journeySteps = [
    { key: 'upload' as const, label: 'Enviar', icon: 'fa-cloud-upload-alt' },
    { key: 'sign' as const, label: 'Firmar', icon: 'fa-pen-nib' },
    { key: 'prepare' as const, label: 'Abrir', icon: 'fa-eye' }
  ];

  private startedAt = 0;
  private progressTimer: ReturnType<typeof setInterval> | null = null;
  private tipTimer: ReturnType<typeof setInterval> | null = null;
  private tipIndex = 0;
  /** Solo 'prepare' fuerza fase; el resto lo mueve el progreso estimado. */
  private prepareForced = false;
  private estimatedDurationMs = 45000;

  constructor(public dialogRef: MatDialogRef<LoadingComponent>) {}

  ngOnInit(): void {
    this.applyMensaje(this.mensaje || 'Procesando...', this.variant);
  }

  applyMensaje(mensaje: string, variant?: LoadingVariant, titulo?: string): void {
    const text = String(mensaje || 'Procesando...').trim();
    this.mensaje = text;
    this.variant = variant ?? this.inferVariant(text);
    this.applyPreset(this.variant, text, titulo);

    const wasJourney = this.isSignJourney;
    this.isSignJourney = this.variant === 'sign';
    if (this.isSignJourney) {
      this.parseProgressFromMessage(text);
      if (!wasJourney) {
        this.resetJourneyMotion();
        this.startJourneyTimers();
      }
      this.syncPhaseFromProgress();
      this.syncPhaseMessage();
    } else {
      this.stopJourneyTimers();
    }
  }

  /** Actualiza el viaje de firma (fase, archivo, progreso). */
  applySignJourney(state: SignJourneyState): void {
    this.variant = 'sign';
    this.isSignJourney = true;
    this.applyPreset('sign', this.mensaje || PHASE_MESSAGES.upload);

    if (state.current != null) {
      this.currentDoc = state.current;
    }
    if (state.total != null) {
      this.totalDocs = state.total;
    }
    if (state.fileName !== undefined) {
      this.fileName = (state.fileName || '').trim();
    }
    if (state.fileSizeBytes !== undefined) {
      this.fileSizeBytes = state.fileSizeBytes ?? null;
      this.estimatedDurationMs = this.estimateDurationMs(this.fileSizeBytes);
    }

    if (state.phase === 'upload') {
      // Inicio / nuevo documento: reinicia el recorrido estimado.
      this.prepareForced = false;
      this.resetJourneyMotion();
    } else if (state.phase === 'prepare') {
      // Respuesta del back: saltamos a preparar vista y aceleramos el avance.
      this.prepareForced = true;
      this.phase = 'prepare';
      this.progressPercent = Math.max(this.progressPercent, SIGN_UNTIL + 4);
    } else if (state.phase === 'sign') {
      this.prepareForced = false;
      this.progressPercent = Math.max(this.progressPercent, UPLOAD_UNTIL + 2);
    }

    this.startJourneyTimers();
    this.syncPhaseFromProgress();
    this.syncPhaseMessage();
  }

  get activeStepIndex(): number {
    return this.journeySteps.findIndex((s) => s.key === this.phase);
  }

  /** Paso ya recorrido por el documento (pasa a verde). */
  isStepDone(index: number): boolean {
    if (index === 0) {
      return this.progressPercent >= UPLOAD_UNTIL;
    }
    if (index === 1) {
      return this.prepareForced || this.progressPercent >= SIGN_UNTIL;
    }
    // Abrir: verde al llegar / preparar vista
    return this.prepareForced && this.progressPercent >= 92;
  }

  isStepActive(index: number): boolean {
    return index === this.activeStepIndex && !this.isStepDone(index);
  }

  /**
   * Posición del documento en el track, alineada a los centros de Enviar → Firmar → Abrir.
   * El % de progreso es estimado; esto solo traduce ese avance a movimiento visible.
   */
  get packetLeftPercent(): number {
    const p0 = 8;
    const p1 = 50;
    const p2 = 92;
    const p = this.progressPercent;
    if (p <= UPLOAD_UNTIL) {
      return p0 + (p / UPLOAD_UNTIL) * (p1 - p0);
    }
    if (p <= SIGN_UNTIL) {
      return p1 + ((p - UPLOAD_UNTIL) / (SIGN_UNTIL - UPLOAD_UNTIL)) * (p2 - p1) * 0.82;
    }
    const t = Math.min(1, (p - SIGN_UNTIL) / 22);
    return p1 + 0.82 * (p2 - p1) + t * 0.18 * (p2 - p1);
  }

  /**
   * Relleno de la línea sincronizado con el documento viajero.
   * La línea CSS va de 12% a 88% del track; el sobre usa left absoluto.
   */
  get lineFillPercent(): number {
    const lineStart = 12;
    const lineSpan = 76;
    return Math.max(0, Math.min(100, ((this.packetLeftPercent - lineStart) / lineSpan) * 100));
  }

  /**
   * Color del documento: naranja al inicio → verde al acercarse a Abrir.
   */
  get packetStyle(): Record<string, string> {
    const t = Math.min(1, Math.max(0, this.progressPercent / 96));
    // #f35b04 → #16a34a
    const r = Math.round(243 + (22 - 243) * t);
    const g = Math.round(91 + (163 - 91) * t);
    const b = Math.round(4 + (74 - 4) * t);
    return {
      left: `${this.packetLeftPercent}%`,
      background: `rgb(${r}, ${g}, ${b})`,
      boxShadow: `0 6px 14px rgba(${r}, ${g}, ${b}, 0.35)`
    };
  }

  get docProgressLabel(): string {
    if (this.totalDocs > 1 && this.currentDoc > 0) {
      return `Documento ${this.currentDoc} de ${this.totalDocs}`;
    }
    return '';
  }

  get fileSizeLabel(): string {
    if (this.fileSizeBytes == null || this.fileSizeBytes <= 0) {
      return '';
    }
    const mb = this.fileSizeBytes / (1024 * 1024);
    if (mb >= 1) {
      return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
    }
    const kb = this.fileSizeBytes / 1024;
    return `${Math.max(1, Math.round(kb))} KB`;
  }

  ngOnDestroy(): void {
    this.stopJourneyTimers();
  }

  private resetJourneyMotion(): void {
    this.startedAt = Date.now();
    this.progressPercent = 2;
    this.phase = 'upload';
    this.estimatedDurationMs = this.estimateDurationMs(this.fileSizeBytes);
  }

  /**
   * Estimación heurística: el back no reporta progreso.
   * ~22s base + ~0.55s por MB, entre 28s y 140s.
   */
  private estimateDurationMs(bytes: number | null): number {
    const mb = bytes && bytes > 0 ? bytes / (1024 * 1024) : 8;
    const ms = 22000 + mb * 550;
    return Math.min(140000, Math.max(28000, ms));
  }

  private syncPhaseMessage(): void {
    if (!this.isSignJourney) {
      return;
    }
    const phaseMsg = PHASE_MESSAGES[this.phase];
    if (this.fileName) {
      this.mensaje = `${phaseMsg.replace('…', '')} (${this.fileName})…`;
    } else if (this.totalDocs > 1 && this.currentDoc > 0) {
      this.mensaje = `${phaseMsg.replace('…', '')} · ${this.currentDoc}/${this.totalDocs}…`;
    } else {
      this.mensaje = phaseMsg;
    }
  }

  private parseProgressFromMessage(text: string): void {
    const m = /(\d+)\s*de\s*(\d+)/i.exec(text);
    if (m) {
      this.currentDoc = parseInt(m[1], 10);
      this.totalDocs = parseInt(m[2], 10);
    }
    const named = /:\s*(.+)$/.exec(text);
    if (named?.[1]) {
      this.fileName = named[1].replace(/\.\.\.$/, '').trim();
    } else if (/Firmando\s+(.+)\.\.\.$/i.test(text) && !m) {
      this.fileName = text.replace(/^Firmando\s+/i, '').replace(/\.\.\.$/, '').trim();
    }
  }

  private startJourneyTimers(): void {
    if (!this.startedAt) {
      this.startedAt = Date.now();
    }
    if (!this.progressTimer) {
      this.tickProgress();
      this.progressTimer = setInterval(() => this.tickProgress(), 120);
    }
    if (!this.tipTimer) {
      this.tipTimer = setInterval(() => {
        this.tipIndex = (this.tipIndex + 1) % SIGN_TIPS.length;
        this.currentTip = SIGN_TIPS[this.tipIndex];
      }, 8000);
    }
  }

  private stopJourneyTimers(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.tipTimer) {
      clearInterval(this.tipTimer);
      this.tipTimer = null;
    }
    this.startedAt = 0;
    this.prepareForced = false;
    this.progressPercent = 0;
  }

  /**
   * Progreso asimptótico: avanza de forma visible y se frena cerca del 90%
   * hasta que llega la respuesta real (prepare).
   */
  private tickProgress(): void {
    if (!this.isSignJourney) {
      return;
    }

    const elapsed = Math.max(0, Date.now() - this.startedAt);
    const t = Math.min(1, elapsed / this.estimatedDurationMs);
    // Ease-out: rápido al inicio (subida), más lento en la firma.
    const eased = 1 - Math.pow(1 - t, 1.65);
    let target = eased * SOFT_CAP;

    if (this.prepareForced) {
      // Acelera hacia ~96% mientras convierte el blob en el front.
      const prepareBoost = Math.min(96, this.progressPercent + 1.8);
      target = Math.max(target, prepareBoost, SIGN_UNTIL + 6);
    }

    // Suaviza el movimiento (lerp).
    const next = this.progressPercent + (target - this.progressPercent) * 0.18;
    this.progressPercent = Math.min(this.prepareForced ? 96 : SOFT_CAP, Math.max(this.progressPercent, next));

    const prevPhase = this.phase;
    this.syncPhaseFromProgress();
    if (prevPhase !== this.phase) {
      this.syncPhaseMessage();
    }
  }

  private syncPhaseFromProgress(): void {
    if (this.prepareForced) {
      this.phase = 'prepare';
      return;
    }
    if (this.progressPercent < UPLOAD_UNTIL) {
      this.phase = 'upload';
    } else if (this.progressPercent < SIGN_UNTIL) {
      this.phase = 'sign';
    } else {
      // Cerca del final estimado, aún sin respuesta: se mantiene en firmar
      // (prepare solo cuando el back responde).
      this.phase = 'sign';
    }
  }

  private applyPreset(variant: LoadingVariant, mensaje: string, tituloOverride?: string): void {
    const preset = PRESETS[variant];
    this.iconClass = preset.iconClass;
    this.useSvgIcon = preset.useSvgIcon;
    this.svgIconPath = preset.svgIconPath;
    this.coreIconClass = preset.coreIconClass;

    if (tituloOverride?.trim()) {
      this.titulo = tituloOverride.trim();
      return;
    }
    if (variant === 'admin') {
      this.titulo = this.tituloDesdeMensaje(mensaje) || preset.title;
      return;
    }
    if (variant === 'sign') {
      this.titulo = preset.title;
      return;
    }
    this.titulo = preset.title;
  }

  private tituloDesdeMensaje(mensaje: string): string {
    return mensaje.replace(/\.\.\.$/, '').trim();
  }

  private inferVariant(mensaje: string): LoadingVariant {
    const m = mensaje.toLowerCase();

    if (
      m.includes('identidad') ||
      m.includes('enviando código') ||
      m.includes('reenviando') ||
      m.includes('cambiando contraseña') ||
      m.includes('cargando información')
    ) {
      return 'admin';
    }
    if (m.includes('verificando firmas') || (m.includes('verificando') && m.includes('firma'))) {
      return 'verify';
    }
    if (
      m.includes('firmando') ||
      m.includes('procesando firma') ||
      m.includes('enviando el documento') ||
      m.includes('preparando la vista') ||
      m.includes('aplicando la firma')
    ) {
      return 'sign';
    }
    if (m.includes('cargando certificados')) {
      return 'auth';
    }
    if (
      m.includes('cargando tus solicitudes') ||
      m.includes('estado del trámite') ||
      m.includes('consultando factura') ||
      m.includes('revoc')
    ) {
      return 'gestion';
    }
    if (m.includes('descargando')) {
      return 'download';
    }
    return 'generic';
  }
}
